import React from 'react';
import { t, defineMessages } from '@sm/intl';
import { createURL } from '@sm/utils';
import { TopBarFull } from '../TopBarFull';
import {
  TopBarFullCtaColors,
  TopBarFullUseCaseEnums,
} from '../TopBarFullEnums';
import researchImage from '../assets/UCS_Academic_Research.png';

const DEFAULT_UTSOURCE2 = 'full_misc_upsell_academic_research';
const DEFAULT_URL_BASE = '/pricing/upgrade/';
const DEFAULT_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseAcademicResearchTopBarFull.headline',
    defaultMessage: 'Get better research data with advanced features',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseAcademicResearchTopBarFull.message',
    defaultMessage:
      'Include survey logic, conduct A/B tests, or add more question types with an upgraded plan.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseAcademicResearchTopBarFull.ctaText',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const INITIATED_DRAFTED_UTSOURCE2 = 'full_initiate_upsell_academic_research';
const INITIATED_DRAFTED_URL_BASE = '/pricing/upgrade/';
const INITIATED_DRAFTED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseAcademicResearchTopBarFull.headlineInitiated',
    defaultMessage: 'Get better research data with advanced features',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseAcademicResearchTopBarFull.messageInitiated',
    defaultMessage:
      'Include survey logic, conduct A/B tests, or add more question types with an upgraded plan.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseAcademicResearchTopBarFull.ctaTextInitiated',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const TESTED_ACTIVATED_UTSOURCE2 = 'full_activate_upsell_academic_research';
const TESTED_ACTIVATED_URL_BASE = '/collect/audience/preview';
const TESTED_ACTIVATED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseAcademicResearchTopBarFull.headlineTested',
    defaultMessage: 'Reach your market from around the globe',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseAcademicResearchTopBarFull.messageTested',
    defaultMessage:
      'Gather feedback from a global panel of survey respondents with SurveyMonkey Audience',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseAcademicResearchTopBarFull.ctaTextTested',
    defaultMessage: 'BUY RESPONSES',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

const DEPLOYED_UTSOURCE2 = 'full_deploy_upsell_academic_research';
const DEPLOYED_URL_BASE = '/pricing/upgrade/';
const DEPLOYED_COPY = defineMessages({
  HEADLINE: {
    id: 'UseCaseAcademicResearchTopBarFull.headlineDeployed',
    defaultMessage: 'See your data clearly with advanced exports',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  MESSAGE: {
    id: 'UseCaseAcademicResearchTopBarFull.messageDeployed',
    defaultMessage:
      'Get deeper analysis with crosstabs, SPSS & XLS exports, and stat testing.',
    description: '[Type: Paragraph][Vis.: low] - Top bar full message',
  },
  CTA_TEXT: {
    id: 'UseCaseAcademicResearchTopBarFull.ctaTextDeployed',
    defaultMessage: 'SEE ALL PLANS',
    description: '[Type: Button][Vis.: low] - Top bar full cta text',
  },
});

export type UseCaseAcademicResearchTopBarFullProps = {
  variation?: TopBarFullUseCaseEnums | null;
};

const VariationToCopy = {
  [TopBarFullUseCaseEnums.InitiatedDrafted]: {
    copy: INITIATED_DRAFTED_COPY,
    utSource2: INITIATED_DRAFTED_UTSOURCE2,
    urlBase: INITIATED_DRAFTED_URL_BASE,
  },
  [TopBarFullUseCaseEnums.TestedActivated]: {
    copy: TESTED_ACTIVATED_COPY,
    utSource2: TESTED_ACTIVATED_UTSOURCE2,
    urlBase: TESTED_ACTIVATED_URL_BASE,
  },
  [TopBarFullUseCaseEnums.Deployed]: {
    copy: DEPLOYED_COPY,
    utSource2: DEPLOYED_UTSOURCE2,
    urlBase: DEPLOYED_URL_BASE,
  },
};

export const UseCaseAcademicResearchTopBarFull: React.FC<UseCaseAcademicResearchTopBarFullProps> = ({
  variation,
}) => {
  const [copy, utSource2, urlBase] =
    variation && variation in VariationToCopy
      ? [
          VariationToCopy[variation].copy,
          VariationToCopy[variation].utSource2,
          VariationToCopy[variation].urlBase,
        ]
      : [DEFAULT_COPY, DEFAULT_UTSOURCE2, DEFAULT_URL_BASE];
  return (
    <TopBarFull
      headline={t(copy.HEADLINE)}
      message={t(copy.MESSAGE)}
      image={() => <img src={researchImage} aria-hidden="true" />}
      ctaText={t(copy.CTA_TEXT)}
      ctaLink={createURL(urlBase, {
        ut_source: 'topbar',
        ut_source2: utSource2,
      })}
      ctaNewTab={true}
      ctaColor={TopBarFullCtaColors.alt}
    />
  );
};

export default UseCaseAcademicResearchTopBarFull;
